import React, { useState, useEffect } from 'react';
import { ref, push, set, update, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';
import { database, auth, app } from '../firebase';

const storage = getStorage(app);

// URL da API MySQL no Hostinger
const MYSQL_API_URL = 'https://nutrisync.dgapp.com.br/api/content.php';
const UPLOAD_API_URL = 'https://nutrisync.dgapp.com.br/api/upload-content-image.php';

// Função para obter data local no formato YYYY-MM-DD (corrige problema de fuso horário)
function getLocalDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Função para enviar imagem para o Hostinger
async function uploadImageToHostinger(file) {
    console.log('[Upload] Iniciando upload para Hostinger:', file.name);
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(UPLOAD_API_URL, {
        method: 'POST',
        body: formData
    });

    const result = await response.json();
    console.log('[Upload] Resposta:', result);
    
    if (!result.success) {
        throw new Error(result.error || 'Erro ao fazer upload da imagem');
    }

    return result.url;
}

// Função para enviar conteúdo para MySQL
async function sendToMySQL(contentData) {
    try {
        console.log('[MySQL] Enviando para MySQL:', contentData);
        console.log('[MySQL] UIDs destinatários:', contentData.sentTo);
        
        const response = await fetch(MYSQL_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: contentData.type,
                title: contentData.title,
                content: contentData.content || '',
                url: contentData.url || '',
                pdfUrl: contentData.pdfUrl || '',
                imageUrl: contentData.imageUrl || '',
                sentTo: contentData.sentTo,
                createdBy: contentData.createdBy || 'admin',
                scheduledDate: contentData.scheduledDate || null
            }),
        });

        const result = await response.json();
        console.log('[MySQL] Resposta:', result);
        
        if (!result.success) {
            console.error('[MySQL] Erro:', result.error);
        }
        
        return result;
    } catch (error) {
        console.error('[MySQL] Erro ao enviar:', error);
        // Não falhar se MySQL der erro - Firebase é o principal por enquanto
        return { success: false, error: error.message };
    }
}

export default function ContentSender({ onClose, editingContent = null }) {
    const [contentType, setContentType] = useState(editingContent?.type || 'video'); // 'video', 'card', 'article'
    const [title, setTitle] = useState(editingContent?.title || '');
    const [content, setContent] = useState(editingContent?.content || '');
    const [url, setUrl] = useState(editingContent?.url || '');
    const [pdfUrl, setPdfUrl] = useState(editingContent?.pdfUrl || '');
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [pdfInputType, setPdfInputType] = useState('url'); // 'url' ou 'file'
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(editingContent?.imageUrl || null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [selectedNutritionists, setSelectedNutritionists] = useState(editingContent?.sentTo || []);
    const [nutritionists, setNutritionists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scheduledDate, setScheduledDate] = useState(editingContent?.scheduledDate ? new Date(editingContent.scheduledDate).toISOString().split('T')[0] : '');
    const [scheduledTime, setScheduledTime] = useState(editingContent?.scheduledDate ? new Date(editingContent.scheduledDate).toISOString().split('T')[1].substring(0, 5) : '');

    useEffect(() => {
        const usersRef = ref(database, 'users');
        const unsubscribe = onValue(usersRef, (snapshot) => {
            const users = snapshot.val() || {};
            const nutritionistsList = Object.entries(users)
                .filter(([id, data]) => data.role === 'nutritionist' && !data.blocked)
                .map(([id, data]) => ({
                    id,
                    name: data.name || data.email || 'Sem nome',
                    email: data.email
                }));
            setNutritionists(nutritionistsList);
            console.log('[ContentSender] Nutricionistas carregados:', nutritionistsList.map(n => ({ id: n.id, name: n.name })));
        });

        return () => unsubscribe();
    }, []);

    // Atualizar estados quando editingContent mudar
    useEffect(() => {
        if (editingContent) {
            setContentType(editingContent.type || 'video');
            setTitle(editingContent.title || '');
            setContent(editingContent.content || '');
            setUrl(editingContent.url || '');
            setPdfUrl(editingContent.pdfUrl || '');
            setImagePreview(editingContent.imageUrl || null);
            setSelectedImage(null);
            setSelectedPdf(null);
            setPdfFileName(editingContent.pdfFileName || '');
            // Detectar se é URL (começa com http) ou arquivo do Storage
            if (editingContent.pdfUrl) {
                const isUrl = editingContent.pdfUrl.startsWith('http://') || editingContent.pdfUrl.startsWith('https://');
                setPdfInputType(isUrl ? 'url' : 'file');
            } else {
                setPdfInputType('url');
            }
            // Garantir que sentTo seja um array
            const sentToArray = Array.isArray(editingContent.sentTo) 
                ? editingContent.sentTo 
                : (editingContent.sentTo ? Object.values(editingContent.sentTo) : []);
            setSelectedNutritionists(sentToArray);
            if (editingContent.scheduledDate) {
                const date = new Date(editingContent.scheduledDate);
                setScheduledDate(date.toISOString().split('T')[0]);
                setScheduledTime(date.toISOString().split('T')[1].substring(0, 5));
            } else {
                setScheduledDate('');
                setScheduledTime('');
            }
        } else {
            // Resetar para valores padrão quando não estiver editando
            setContentType('video');
            setTitle('');
            setContent('');
            setUrl('');
            setPdfUrl('');
            setSelectedPdf(null);
            setPdfFileName('');
            setPdfInputType('url');
            setImagePreview(null);
            setSelectedImage(null);
            setSelectedNutritionists([]);
            setScheduledDate('');
            setScheduledTime('');
        }
    }, [editingContent]);

    const handleToggleNutritionist = (nutritionistId) => {
        setSelectedNutritionists(prev =>
            prev.includes(nutritionistId)
                ? prev.filter(id => id !== nutritionistId)
                : [...prev, nutritionistId]
        );
    };

    const handleSelectAll = () => {
        if (selectedNutritionists.length === nutritionists.length) {
            setSelectedNutritionists([]);
        } else {
            setSelectedNutritionists(nutritionists.map(n => n.id));
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tipo de arquivo
        if (!file.type.startsWith('image/')) {
            alert('Por favor, selecione um arquivo de imagem.');
            return;
        }

        // Validar tamanho (5MB)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('A imagem deve ter no máximo 5MB.');
            return;
        }

        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handlePdfSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tipo de arquivo
        if (file.type !== 'application/pdf') {
            alert('Por favor, selecione um arquivo PDF.');
            return;
        }

        // Validar tamanho (10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('O PDF deve ter no máximo 10MB.');
            return;
        }

        setSelectedPdf(file);
        setPdfFileName(file.name);
        setPdfUrl(''); // Limpar URL se houver
    };

    const handleRemovePdf = () => {
        setSelectedPdf(null);
        setPdfFileName('');
    };

    const handleSend = async () => {
        if (!title || selectedNutritionists.length === 0) {
            alert('Preencha o título e selecione pelo menos um nutricionista');
            return;
        }

        if (contentType === 'video' && !url) {
            alert('Informe a URL do vídeo');
            return;
        }

        if (contentType === 'article' && !content) {
            alert('Informe o conteúdo do artigo');
            return;
        }

        setLoading(true);
        try {
            // Calcular data planejada se fornecida
            let scheduledDateISO = null;
            if (scheduledDate && scheduledTime) {
                const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
                // Para novos conteúdos, a data deve ser no futuro
                // Para edições, permitir manter a data existente ou definir uma nova no futuro
                if (editingContent) {
                    // Se está editando, permite manter a data existente ou mudar para uma futura
                    scheduledDateISO = scheduledDateTime.toISOString();
                } else if (scheduledDateTime > new Date()) {
                    scheduledDateISO = scheduledDateTime.toISOString();
                } else {
                    alert('A data e hora planejada deve ser no futuro');
                    setLoading(false);
                    return;
                }
            } else if (scheduledDate && !scheduledTime) {
                // Se tem data mas não tem hora, usar 00:00
                const scheduledDateTime = new Date(`${scheduledDate}T00:00`);
                scheduledDateISO = scheduledDateTime.toISOString();
            }

            // Fazer upload da imagem se houver nova imagem selecionada
            let imageUrl = editingContent?.imageUrl || null;
            if (contentType === 'card' && selectedImage) {
                setUploadingImage(true);
                try {
                    // Upload para Hostinger
                    console.log('Enviando imagem para Hostinger...');
                    imageUrl = await uploadImageToHostinger(selectedImage);
                    console.log('Imagem enviada com sucesso:', imageUrl);
                    
                    // Fallback para Firebase se Hostinger falhar (opcional, mas bom para garantir)
                    /* 
                    const imageRef = storageRef(storage, `cards/${auth.currentUser?.uid || 'admin'}/${Date.now()}_${selectedImage.name}`);
                    await uploadBytes(imageRef, selectedImage);
                    imageUrl = await getDownloadURL(imageRef);
                    */
                } catch (error) {
                    console.error('Erro ao fazer upload da imagem:', error);
                    alert('Erro ao fazer upload da imagem: ' + error.message);
                    setLoading(false);
                    setUploadingImage(false);
                    return;
                } finally {
                    setUploadingImage(false);
                }
            }

            // Fazer upload do PDF se houver PDF selecionado
            let finalPdfUrl = editingContent?.pdfUrl || null;
            let finalPdfFileName = editingContent?.pdfFileName || null;
            
            if (contentType === 'article' && selectedPdf) {
                setUploadingPdf(true);
                try {
                    const pdfRef = storageRef(storage, `articles/${auth.currentUser?.uid || 'admin'}/${Date.now()}_${selectedPdf.name}`);
                    await uploadBytes(pdfRef, selectedPdf);
                    finalPdfUrl = await getDownloadURL(pdfRef);
                    finalPdfFileName = selectedPdf.name;
                } catch (error) {
                    console.error('Erro ao fazer upload do PDF:', error);
                    alert('Erro ao fazer upload do PDF. Tente novamente.');
                    setLoading(false);
                    setUploadingPdf(false);
                    return;
                } finally {
                    setUploadingPdf(false);
                }
            } else if (contentType === 'article' && pdfInputType === 'url' && pdfUrl) {
                finalPdfUrl = pdfUrl;
                // Manter o nome do arquivo se já existir, ou tentar extrair da URL
                if (!finalPdfFileName && pdfUrl) {
                    try {
                        const urlPath = new URL(pdfUrl).pathname;
                        const fileName = urlPath.split('/').pop();
                        if (fileName && fileName.endsWith('.pdf')) {
                            finalPdfFileName = fileName;
                        }
                    } catch (e) {
                        // Se não conseguir extrair, deixa null
                    }
                }
            }

            if (editingContent) {
                // Modo edição: usar update() para preservar dados existentes
                if (!editingContent.id) {
                    alert('Erro: ID do conteúdo não encontrado');
                    setLoading(false);
                    return;
                }

                // Preparar os dados para atualização
                const updateData = {};
                
                // Sempre atualizar estes campos
                updateData.type = contentType;
                updateData.title = title;
                updateData.updatedAt = new Date().toISOString();
                
                // Campos condicionais baseados no tipo
                if (contentType === 'card' || contentType === 'article') {
                    updateData.content = content;
                } else {
                    updateData.content = '';
                }
                
                if (contentType === 'video') {
                    updateData.url = url;
                } else {
                    updateData.url = '';
                }
                
                if (contentType === 'article') {
                    updateData.pdfUrl = finalPdfUrl || '';
                    updateData.pdfFileName = finalPdfFileName || null;
                } else {
                    updateData.pdfUrl = '';
                    updateData.pdfFileName = null;
                }
                
                if (contentType === 'card') {
                    updateData.imageUrl = imageUrl;
                } else {
                    updateData.imageUrl = null;
                }
                
                // Sempre atualizar sentTo (para garantir que seja um array)
                // Firebase às vezes converte arrays em objetos
                if (selectedNutritionists && selectedNutritionists.length > 0) {
                    // Garantir que seja sempre um array
                    updateData.sentTo = [...selectedNutritionists];
                } else if (editingContent.sentTo) {
                    // Manter a lista existente como array
                    const existingSentTo = Array.isArray(editingContent.sentTo) 
                        ? editingContent.sentTo 
                        : Object.values(editingContent.sentTo);
                    updateData.sentTo = [...existingSentTo];
                }
                
                // Atualizar scheduledDate se houver mudança
                if (scheduledDate && scheduledTime) {
                    updateData.scheduledDate = scheduledDateISO;
                } else if (!scheduledDate && !scheduledTime && editingContent.scheduledDate) {
                    // Se limpou os campos de data, remover o agendamento
                    updateData.scheduledDate = null;
                }
                // Se não mudou a data (campos vazios mas tinha data antes), não atualiza

                console.log('Atualizando conteúdo:', editingContent.id, updateData);

                const contentRef = ref(database, `contentForNutritionists/${editingContent.id}`);
                await update(contentRef, updateData);
                alert(`Conteúdo "${title}" atualizado com sucesso!`);
            } else {
                // Modo criação: criar novo conteúdo
                const contentData = {
                    type: contentType,
                    title,
                    content: contentType === 'card' || contentType === 'article' ? content : '',
                    url: contentType === 'video' ? url : '',
                    pdfUrl: contentType === 'article' ? (finalPdfUrl || '') : '',
                    pdfFileName: contentType === 'article' ? (finalPdfFileName || null) : null,
                    imageUrl: contentType === 'card' ? imageUrl : null,
                    sentTo: [...selectedNutritionists], // Forçar array
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    createdBy: auth.currentUser?.uid || 'admin'
                };
                
                // Só adicionar scheduledDate se houver valor
                if (scheduledDateISO) {
                    contentData.scheduledDate = scheduledDateISO;
                }
                
                console.log('Criando novo conteúdo:', contentData);

                // Enviar para Firebase
                const contentRef = ref(database, 'contentForNutritionists');
                const newContentRef = push(contentRef);
                await set(newContentRef, contentData);

                // Enviar também para MySQL (Hostinger)
                const mysqlResult = await sendToMySQL(contentData);
                if (mysqlResult.success) {
                    console.log('[MySQL] Conteúdo salvo com ID:', mysqlResult.contentId);
                }

                // Criar notificações para cada nutricionista apenas se não for agendado
                if (!scheduledDateISO) {
                    const notificationsRef = ref(database, 'notifications');
                    const notificationPromises = selectedNutritionists.map(nutritionistId => {
                        const notificationRef = push(notificationsRef);
                        return set(notificationRef, {
                            userId: nutritionistId,
                            type: 'content',
                            title: `Novo ${contentType === 'video' ? 'vídeo' : contentType === 'card' ? 'card' : 'artigo'}: ${title}`,
                            message: `Você recebeu um novo conteúdo: ${title}`,
                            contentId: newContentRef.key,
                            read: false,
                            createdAt: new Date().toISOString()
                        });
                    });

                    await Promise.all(notificationPromises);
                }

                if (scheduledDateISO) {
                    alert(`Conteúdo agendado para ${new Date(scheduledDateISO).toLocaleString('pt-BR')} para ${selectedNutritionists.length} nutricionista(s)!`);
                } else {
                    alert(`Conteúdo enviado para ${selectedNutritionists.length} nutricionista(s) com sucesso!`);
                }
            }
            onClose();
        } catch (error) {
            console.error('Erro ao enviar conteúdo:', error);
            alert('Erro ao enviar conteúdo. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                background: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '16px',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                    <h2 style={{ margin: 0 }}>{editingContent ? 'Editar Conteúdo' : 'Enviar Conteúdo para Nutricionistas'}</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-main)',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            padding: '0.5rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                        Tipo de Conteúdo
                    </label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {['video', 'card', 'article'].map(type => (
                            <button
                                key={type}
                                onClick={() => setContentType(type)}
                                className={`btn ${contentType === type ? 'btn-primary' : ''}`}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem',
                                    background: contentType === type ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                                    color: contentType === type ? '#0f172a' : 'var(--text-main)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: contentType === type ? 600 : 400
                                }}
                            >
                                {type === 'video' ? '🎥 Vídeo' : type === 'card' ? '🃏 Card' : '📄 Artigo'}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                        Título *
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Título do conteúdo"
                        style={{ width: '100%', padding: '0.75rem', marginBottom: 0 }}
                    />
                </div>

                {contentType === 'video' && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                            URL do Vídeo *
                        </label>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://youtube.com/... ou https://vimeo.com/..."
                            style={{ width: '100%', padding: '0.75rem', marginBottom: 0 }}
                        />
                    </div>
                )}

                {contentType === 'card' && (
                    <>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                                Descrição
                            </label>
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Descrição do card"
                                style={{ width: '100%', padding: '0.75rem', minHeight: '100px', marginBottom: 0 }}
                            />
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                                Imagem do Card (opcional)
                            </label>
                            {!imagePreview ? (
                                <div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageSelect}
                                        style={{ display: 'none' }}
                                        id="card-image-input"
                                    />
                                    <label
                                        htmlFor="card-image-input"
                                        style={{
                                            display: 'inline-block',
                                            padding: '0.75rem 1.5rem',
                                            background: 'rgba(255,255,255,0.1)',
                                            color: 'var(--text-main)',
                                            border: '1px dashed rgba(255,255,255,0.3)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            width: '100%',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                                        }}
                                    >
                                        📷 Selecionar Imagem
                                    </label>
                                    <small style={{ 
                                        display: 'block', 
                                        marginTop: '0.5rem', 
                                        color: 'var(--text-muted)',
                                        fontSize: '0.85rem'
                                    }}>
                                        Máximo 5MB. Formatos: JPG, PNG, GIF
                                    </small>
                                </div>
                            ) : (
                                <div style={{
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{
                                            width: '100%',
                                            maxHeight: '300px',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                    />
                                    <button
                                        onClick={handleRemoveImage}
                                        style={{
                                            position: 'absolute',
                                            top: '0.5rem',
                                            right: '0.5rem',
                                            background: 'rgba(0, 0, 0, 0.7)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '32px',
                                            height: '32px',
                                            cursor: 'pointer',
                                            fontSize: '1.2rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 0, 0, 0.8)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {contentType === 'article' && (
                    <>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                                Conteúdo do Artigo *
                            </label>
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Conteúdo do artigo científico"
                                style={{ width: '100%', padding: '0.75rem', minHeight: '150px', marginBottom: 0 }}
                            />
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <label style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                                    PDF do Artigo (opcional)
                                </label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPdfInputType('url');
                                            setSelectedPdf(null);
                                            setPdfFileName('');
                                        }}
                                        style={{
                                            padding: '0.4rem 0.8rem',
                                            fontSize: '0.85rem',
                                            background: pdfInputType === 'url' ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                                            color: pdfInputType === 'url' ? '#0f172a' : 'var(--text-main)',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        🔗 URL
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPdfInputType('file');
                                            setPdfUrl('');
                                        }}
                                        style={{
                                            padding: '0.4rem 0.8rem',
                                            fontSize: '0.85rem',
                                            background: pdfInputType === 'file' ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                                            color: pdfInputType === 'file' ? '#0f172a' : 'var(--text-main)',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        📄 Upload
                                    </button>
                                </div>
                            </div>
                            
                            {pdfInputType === 'url' ? (
                                <input
                                    type="url"
                                    value={pdfUrl}
                                    onChange={(e) => setPdfUrl(e.target.value)}
                                    placeholder="https://..."
                                    style={{ width: '100%', padding: '0.75rem', marginBottom: 0 }}
                                />
                            ) : (
                                <div>
                                    {!selectedPdf ? (
                                        <>
                                            <input
                                                type="file"
                                                accept="application/pdf"
                                                onChange={handlePdfSelect}
                                                style={{ display: 'none' }}
                                                id="pdf-file-input"
                                            />
                                            <label
                                                htmlFor="pdf-file-input"
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '0.75rem 1.5rem',
                                                    background: 'rgba(255,255,255,0.1)',
                                                    color: 'var(--text-main)',
                                                    border: '1px dashed rgba(255,255,255,0.3)',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    width: '100%',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                                                }}
                                            >
                                                📄 Selecionar PDF
                                            </label>
                                            <small style={{ 
                                                display: 'block', 
                                                marginTop: '0.5rem', 
                                                color: 'var(--text-muted)',
                                                fontSize: '0.85rem'
                                            }}>
                                                Máximo 10MB. Formato: PDF
                                            </small>
                                        </>
                                    ) : (
                                        <div style={{
                                            position: 'relative',
                                            padding: '1rem',
                                            background: 'rgba(0, 0, 0, 0.2)',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem'
                                        }}>
                                            <span style={{ fontSize: '2rem' }}>📄</span>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ color: 'var(--text-main)', fontWeight: 500, marginBottom: '0.25rem' }}>
                                                    {pdfFileName}
                                                </div>
                                                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                                    {(selectedPdf.size / (1024 * 1024)).toFixed(2)} MB
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleRemovePdf}
                                                style={{
                                                    background: 'rgba(239, 68, 68, 0.2)',
                                                    color: '#ef4444',
                                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                                    borderRadius: '6px',
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                                                    e.target.style.borderColor = '#ef4444';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                                                }}
                                            >
                                                Remover
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <label style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                            Nutricionistas Destinatários *
                        </label>
                        <button
                            onClick={handleSelectAll}
                            className="btn"
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.85rem',
                                background: 'rgba(255,255,255,0.1)',
                                color: 'var(--text-main)',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                        >
                            {selectedNutritionists.length === nutritionists.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                        </button>
                    </div>
                    <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.2)'
                    }}>
                        {nutritionists.length === 0 ? (
                            <div style={{ 
                                padding: '1rem', 
                                textAlign: 'center', 
                                color: 'var(--text-muted)',
                                fontSize: '0.9rem'
                            }}>
                                Nenhum nutricionista disponível
                            </div>
                        ) : (
                            nutritionists.map(nutritionist => (
                                <div
                                    key={nutritionist.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem',
                                        marginBottom: '0.25rem',
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        transition: 'all 0.2s',
                                        background: selectedNutritionists.includes(nutritionist.id) 
                                            ? 'rgba(0, 208, 156, 0.15)' 
                                            : 'transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!selectedNutritionists.includes(nutritionist.id)) {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!selectedNutritionists.includes(nutritionist.id)) {
                                            e.currentTarget.style.background = 'transparent';
                                        }
                                    }}
                                    onClick={() => handleToggleNutritionist(nutritionist.id)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedNutritionists.includes(nutritionist.id)}
                                        onChange={() => handleToggleNutritionist(nutritionist.id)}
                                        style={{ 
                                            margin: 0,
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer',
                                            accentColor: 'var(--primary)'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span style={{ 
                                        flex: 1,
                                        color: 'var(--text-main)',
                                        fontSize: '0.95rem'
                                    }}>
                                        {nutritionist.name}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                    <small style={{ 
                        color: 'var(--text-muted)', 
                        marginTop: '0.5rem', 
                        display: 'block',
                        fontSize: '0.85rem'
                    }}>
                        {selectedNutritionists.length} de {nutritionists.length} selecionado(s)
                    </small>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                        Data e Hora Planejada (opcional)
                    </label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <input
                                type="date"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                min={getLocalDateString()}
                                style={{ 
                                    width: '100%', 
                                    padding: '0.75rem', 
                                    marginBottom: 0,
                                    background: 'rgba(0, 0, 0, 0.2)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '8px',
                                    color: 'var(--text-main)',
                                    fontFamily: 'inherit'
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <input
                                type="time"
                                value={scheduledTime}
                                onChange={(e) => setScheduledTime(e.target.value)}
                                disabled={!scheduledDate}
                                style={{ 
                                    width: '100%', 
                                    padding: '0.75rem', 
                                    marginBottom: 0,
                                    background: scheduledDate ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '8px',
                                    color: scheduledDate ? 'var(--text-main)' : 'var(--text-muted)',
                                    fontFamily: 'inherit',
                                    cursor: scheduledDate ? 'text' : 'not-allowed'
                                }}
                            />
                        </div>
                    </div>
                    <small style={{ 
                        color: 'var(--text-muted)', 
                        marginTop: '0.5rem', 
                        display: 'block',
                        fontSize: '0.85rem'
                    }}>
                        {scheduledDate && scheduledTime 
                            ? `Conteúdo será disponibilizado em ${new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString('pt-BR')}`
                            : 'Deixe em branco para enviar imediatamente'}
                    </small>
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        className="btn"
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            color: 'var(--text-main)'
                        }}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSend}
                        className="btn btn-primary"
                        disabled={loading || uploadingImage || uploadingPdf}
                    >
                        {uploadingImage ? 'Enviando imagem...' : uploadingPdf ? 'Enviando PDF...' : loading ? (editingContent ? 'Salvando...' : 'Enviando...') : (editingContent ? 'Salvar Alterações' : 'Enviar Conteúdo')}
                    </button>
                </div>
            </div>
        </div>
    );
}




